//src/components/Dashboard.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Header from './Header';
import TaskDetailsModal from './TaskDetailsModal';

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [userTeams, setUserTeams] = useState([]);
  const [userTasks, setUserTasks] = useState([]);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [completedTasksCount, setCompletedTasksCount] = useState(0);
  const [pendingTasksCount, setPendingTasksCount] = useState(0);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const [newlyAssignedTasks, setNewlyAssignedTasks] = useState([]); // Add state for newly assigned tasks
  const [stats, setStats] = useState({
    totalTeams: 0,
    totalTasks: 0,
    completedTasks: 0,
    overdueTasks: 0,
    newlyAssignedCount: 0
  });
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
  const [selectedTaskForDetail, setSelectedTaskForDetail] = useState(null);

  // Lấy dữ liệu người dùng
  useEffect(() => {
    const fetchUserData = async () => {
      if (!auth.currentUser) return;

      try {
        setLoading(true);
        const userId = auth.currentUser.uid;
        const functions = getFunctions();

        // Lấy dữ liệu hồ sơ người dùng bằng cloud function
        const getUserProfile = httpsCallable(functions, 'getUserProfile');
        const result = await getUserProfile({ userId });
        const { user, teams, tasks } = result.data;

        setUserData(user);
        setUserTeams(teams);
        
        // Xử lý công việc
        const completed = tasks.filter(task => task.status === 'completed').length;
        const pending = tasks.filter(task => task.status !== 'completed').length;
        
        // Lấy công việc sắp đến hạn (bỏ điều kiện status để hiển thị tất cả công việc sắp đến hạn)
        const today = new Date();
        const nextWeek = new Date(today);
        nextWeek.setDate(today.getDate() + 7);
        
        const upcoming = tasks
          .filter(task => {
            // Kiểm tra đến hạn trong 7 ngày tới và chưa hoàn thành
            const dueDate = calculateDueDate(task.start_date, task.duration);
            return dueDate && dueDate > today && dueDate <= nextWeek && task.status !== 'completed';
          })
          .sort((a, b) => {
            const dueDateA = calculateDueDate(a.start_date, a.duration);
            const dueDateB = calculateDueDate(b.start_date, b.duration);
            return dueDateA - dueDateB;
          })
          .slice(0, 5); // Lấy 5 công việc sắp đến hạn đầu tiên
        
        console.log("Số công việc sắp đến hạn:", upcoming.length);
        
        // Lấy công việc đang thực hiện cho phần Hoạt động gần đây
        const inProgressTasks = tasks
          .filter(task => task.status === 'inProgress')
          .sort((a, b) => {
            // Sắp xếp theo thời gian cập nhật gần đây nhất nếu có, nếu không thì theo hạn chót
            if (a.updatedAt && b.updatedAt) {
              return new Date(b.updatedAt) - new Date(a.updatedAt);
            }
            const dueDateA = calculateDueDate(a.start_date, a.duration);
            const dueDateB = calculateDueDate(b.start_date, b.duration);
            return dueDateA - dueDateB;
          })
          .slice(0, 5); // Lấy 5 công việc đang thực hiện đầu tiên
        
        // ===== Lấy công việc mới được gán (áp dụng cùng logic như công việc sắp đến hạn) =====
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(today.getDate() - 7);
        
        // Kiểm tra xem người dùng hiện tại có phải là admin đặc biệt không
        const specialAdminEmails = ['ha.ttt@greenschool.edu.vn', 'maxdevfs@gmail.com'];
        
        // Lấy email từ nhiều nguồn để đảm bảo chính xác
        const userEmailFromUserData = user?.email || '';
        const userEmailFromAuth = auth.currentUser?.email || '';
        const userEmailFromResult = userData?.email || '';
        
        console.log("Email từ user (API):", userEmailFromUserData);
        console.log("Email từ auth:", userEmailFromAuth);
        console.log("Email từ userData (state):", userEmailFromResult);
        
        // Kiểm tra email từ tất cả các nguồn
        const isSpecialAdmin = specialAdminEmails.includes(userEmailFromUserData) || 
                              specialAdminEmails.includes(userEmailFromAuth) ||
                              specialAdminEmails.includes(userEmailFromResult);
        
        console.log("Người dùng có phải là admin đặc biệt không:", isSpecialAdmin);
        console.log("Tổng số công việc nhận được từ API:", tasks.length);
        
        // Áp dụng cùng logic như "Công việc sắp đến hạn" - không kiểm tra assignee cho admin đặc biệt
        const newlyAssigned = tasks
          .filter(task => {
            // Lọc công việc chưa hoàn thành
            if (task.status === 'completed') return false;
            
            // Nếu là admin đặc biệt, hiển thị tất cả công việc chưa hoàn thành (giống như công việc sắp đến hạn)
            if (isSpecialAdmin) {
              console.log("Admin đặc biệt - bao gồm task:", task.text);
              return true;
            }
            
            // Người dùng thường chỉ xem các công việc được gán cho họ
            const isAssigned = task.assignees && 
                             task.assignees.some(assignee => assignee.uid === userId);
            if (isAssigned) {
              console.log("Người dùng thường - task được gán:", task.text);
            }
            return isAssigned;
          })
          .map(task => {
            // Tìm đội nhóm tương ứng của công việc để hiển thị tên đội
            const team = teams.find(t => t.id === task.teamId);
            return {
              ...task,
              teamName: team ? team.name : 'Không xác định'
            };
          })
          .sort((a, b) => {
            // Ưu tiên sắp xếp theo thời gian tạo nếu có
            const getCreatedAt = (task) => {
              if (task.createdAt) {
                if (task.createdAt instanceof Date) {
                  return task.createdAt;
                } else if (typeof task.createdAt?.toDate === 'function') {
                  return task.createdAt.toDate();
                } else if (task.createdAt?._seconds) {
                  return new Date(task.createdAt._seconds * 1000);
                } else {
                  try {
                    return new Date(task.createdAt);
                  } catch (e) {
                    return null;
                  }
                }
              }
              return null;
            };
            
            const createdAtA = getCreatedAt(a);
            const createdAtB = getCreatedAt(b);
            
            // Nếu cả hai đều có createdAt, sắp xếp theo createdAt
            if (createdAtA && createdAtB) {
              return createdAtB - createdAtA;
            }
            
            // Nếu không có createdAt, sắp xếp theo start_date
            const startDateA = a.start_date instanceof Date ? 
              a.start_date : (typeof a.start_date?.toDate === 'function' ? 
                a.start_date.toDate() : new Date());
                
            const startDateB = b.start_date instanceof Date ? 
              b.start_date : (typeof b.start_date?.toDate === 'function' ? 
                b.start_date.toDate() : new Date());
                
            return startDateB - startDateA;
          })
          .slice(0, 5); // Lấy tối đa 5 công việc
        
        console.log("Số công việc sẽ hiển thị trong mục Công việc mới được gán:", newlyAssigned.length);
        
        setUserTasks(tasks);
        setCompletedTasksCount(completed);
        setPendingTasksCount(pending);
        setUpcomingTasks(upcoming);
        setNewlyAssignedTasks(newlyAssigned);
        
        // Tính toán công việc quá hạn
        const overdue = tasks.filter(task => {
          const dueDate = calculateDueDate(task.start_date, task.duration);
          return dueDate && dueDate < today && task.status !== 'completed';
        }).length;
        
        // Thiết lập thống kê
        setStats({
          totalTeams: teams.length,
          totalTasks: tasks.length,
          completedTasks: completed,
          overdueTasks: overdue,
          newlyAssignedCount: newlyAssigned.length
        });

        // Lấy thông báo mới nhất
        const notifications = [];
        try {
          const notificationsRef = collection(db, 'notifications');
          const q = query(
            notificationsRef,
            where('userId', '==', userId),
            orderBy('createdAt', 'desc'), // Sắp xếp theo thời gian tạo, giảm dần
            limit(5)
          );
          
          const querySnapshot = await getDocs(q);
          querySnapshot.forEach(doc => {
            notifications.push({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate() || new Date()
            });
          });
        } catch (notificationError) {
          console.error('Lỗi khi lấy thông báo:', notificationError);
          // Thử lại không sử dụng orderBy để lấy thông báo
          try {
            const notificationsRef = collection(db, 'notifications');
            const basicQuery = query(
              notificationsRef,
              where('userId', '==', userId),
              limit(10)
            );
            
            const querySnapshot = await getDocs(basicQuery);
            querySnapshot.forEach(doc => {
              notifications.push({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate() || new Date()
              });
            });
            
            // Sắp xếp thủ công ở phía client
            notifications.sort((a, b) => {
              if (!a.createdAt || !b.createdAt) return 0;
              return b.createdAt - a.createdAt;
            });
            
            // Chỉ giữ lại 5 thông báo đầu tiên
            notifications.splice(5);
          } catch (fallbackError) {
            console.error('Lấy thông báo phương án dự phòng cũng thất bại:', fallbackError);
          }
        }
        
        setRecentNotifications(notifications);
      } catch (error) {
        console.error('Lỗi khi lấy dữ liệu bảng điều khiển:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // Tính toán hạn chót công việc
  const calculateDueDate = (startDate, duration) => {
    if (!startDate) return null;
    
    // Xử lý các định dạng startDate khác nhau
    let start;
    
    // Nếu là timestamp Firestore với phương thức toDate
    if (startDate && typeof startDate.toDate === 'function') {
      start = startDate.toDate();
    } 
    // Nếu là đối tượng timestamp được serialize với _seconds
    else if (startDate && startDate._seconds) {
      start = new Date(startDate._seconds * 1000);
    }
    // Nếu đã là đối tượng Date
    else if (startDate instanceof Date) {
      start = startDate;
    }
    // Thử phân tích chuỗi ngày hoặc timestamp
    else {
      start = new Date(startDate);
    }
    
    if (isNaN(start.getTime())) return null; // Ngày không hợp lệ
    
    const dueDate = new Date(start);
    dueDate.setDate(dueDate.getDate() + (parseInt(duration) || 0));
    return dueDate;
  };

  // Định dạng ngày
  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (!(date instanceof Date)) return 'Ngày không hợp lệ';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Lấy ngày tạo công việc
  const getCreationDate = (task) => {
    if (!task.createdAt) return null;
    
    let createdAt;
    if (task.createdAt instanceof Date) {
      createdAt = task.createdAt;
    } else if (typeof task.createdAt?.toDate === 'function') {
      createdAt = task.createdAt.toDate();
    } else if (task.createdAt?._seconds) {
      createdAt = new Date(task.createdAt._seconds * 1000);
    } else {
      try {
        createdAt = new Date(task.createdAt);
      } catch (e) {
        return null;
      }
    }
    
    return createdAt;
  };

  // Chuyển đến chi tiết phòng ban
  const navigateToTeam = (teamId) => {
    navigate(`/Teams`);
  };

  // Chuyển đến danh sách công việc của team
  const navigateToTask = (teamId) => {
    navigate(`/team/${teamId}/tasks`);
  };

  // Tính toán màu tiến độ công việc
  const getProgressColor = (progress) => {
    if (progress < 25) return 'bg-red-500';
    if (progress < 50) return 'bg-yellow-500';
    if (progress < 75) return 'bg-blue-500';
    return 'bg-emerald-500';
  };

  const handleViewTaskDetails = (task) => {
    setSelectedTaskForDetail(task);
    setShowTaskDetailsModal(true);
  };

  const handleCloseTaskDetailsModal = () => {
    setShowTaskDetailsModal(false);
    setSelectedTaskForDetail(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Phần chào mừng */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Xin chào, {userData?.displayName || 'Người dùng'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Đây là tổng quan về không gian làm việc và hoạt động gần đây của bạn
          </p>
        </div>
        
        {/* Lưới thống kê */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {/* Số lượng phòng ban */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
              <i className="fas fa-users text-emerald-600 dark:text-emerald-400 text-xl"></i>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">phòng ban</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalTeams}</div>
            </div>
          </div>
          
          {/* Số lượng công việc */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
              <i className="fas fa-tasks text-blue-600 dark:text-blue-400 text-xl"></i>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Tổng công việc</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalTasks}</div>
            </div>
          </div>
          
          {/* Công việc mới được gán */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
              <i className="fas fa-bell text-blue-600 dark:text-blue-400 text-xl"></i>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Mới được gán</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.newlyAssignedCount}</div>
            </div>
          </div>
          
          {/* Công việc đã hoàn thành */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <i className="fas fa-check-circle text-green-600 dark:text-green-400 text-xl"></i>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Đã hoàn thành</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.completedTasks}
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-1">/ {stats.totalTasks}</span>
              </div>
            </div>
          </div>
          
          {/* Công việc quá hạn */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <i className="fas fa-exclamation-circle text-red-600 dark:text-red-400 text-xl"></i>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Quá hạn</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.overdueTasks}</div>
            </div>
          </div>
        </div>
        
        {/* Nội dung chính - Bố cục 2 cột */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cột trái - Công việc và Hoạt động */}
          <div className="lg:col-span-2 space-y-8">
            {/* Công việc mới được gán trong 7 ngày qua */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">Công việc mới được gán cho bạn</h2>
              </div>
              <div className="p-6">
                {newlyAssignedTasks.length > 0 ? (
                  <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                    {newlyAssignedTasks.map(task => (
                      <li key={task.id} className="py-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <button
                              className="text-sm font-medium text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-200 truncate focus:outline-none bg-transparent border-none p-0 cursor-pointer"
                              onClick={() => navigate(`/team/${task.teamId}/tasks?taskId=${task.id}&from=dashboard`)}
                              title="Xem chi tiết công việc"
                              style={{ textDecoration: 'none' }}
                            >
                              {task.text}
                            </button>
                            <div className="mt-1 flex items-center text-sm text-gray-500 dark:text-gray-400">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 mr-2">
                                <i className="fas fa-star-of-life text-xs mr-1"></i>
                                Mới
                              </span>
                              <i className="fas fa-calendar-alt mr-1.5"></i>
                              <span>Hạn: {formatDate(calculateDueDate(task.start_date, task.duration))}</span>
                              <span className="mx-2">•</span>
                              <i className="fas fa-users mr-1.5"></i>
                              <span>{task.teamName}</span>
                              {getCreationDate(task) && (
                                <>
                                  <span className="mx-2">•</span>
                                  <i className="fas fa-clock mr-1.5"></i>
                                  <span>Được gán: {formatDate(getCreationDate(task))}</span>
                                </>
                              )}
                            </div>
                            <div className="mt-2">
                              <div className="relative w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div 
                                  className={`absolute top-0 left-0 h-full ${getProgressColor(task.progress)}`}
                                  style={{ width: `${task.progress}%` }}
                                ></div>
                              </div>
                              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Hoàn thành {task.progress}%
                              </div>
                            </div>
                          </div>
                          <span className={`ml-3 px-2.5 py-0.5 rounded-full text-xs font-medium
                            ${task.priority === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                              task.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                              'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300'}`}
                          >
                            {task.priority === 'high' ? 'Cao' : 
                             task.priority === 'medium' ? 'Trung bình' : 'Thấp'}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center py-8">
                    <div className="mb-4">
                      <i className="fas fa-inbox text-gray-400 dark:text-gray-500 text-4xl"></i>
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">Không có công việc mới được gán</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Bạn chưa nhận được công việc mới nào trong 7 ngày qua</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Công việc sắp đến hạn */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">Công việc sắp đến hạn trong 7 ngày tới</h2>
                
              </div>
              <div className="p-6">
                {upcomingTasks.length > 0 ? (
                  <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                    {upcomingTasks.map(task => (
                      <li key={task.id} className="py-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <button
                              className="text-sm font-medium text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-200 truncate focus:outline-none bg-transparent border-none p-0 cursor-pointer"
                              onClick={() => navigate(`/team/${task.teamId}/tasks?taskId=${task.id}&from=dashboard`)}
                              title="Xem chi tiết công việc"
                              style={{ textDecoration: 'none' }}
                            >
                              {task.text}
                            </button>
                            <div className="mt-1 flex items-center text-sm text-gray-500 dark:text-gray-400">
                              <i className="fas fa-calendar-alt mr-1.5"></i>
                              <span>Hạn: {formatDate(calculateDueDate(task.start_date, task.duration))}</span>
                              <span className="mx-2">•</span>
                              <i className="fas fa-users mr-1.5"></i>
                              <span>{task.teamName}</span>
                            </div>
                            <div className="mt-2">
                              <div className="relative w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div 
                                  className={`absolute top-0 left-0 h-full ${getProgressColor(task.progress)}`}
                                  style={{ width: `${task.progress}%` }}
                                ></div>
                              </div>
                              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Hoàn thành {task.progress}%
                              </div>
                            </div>
                          </div>
                          <span className={`ml-3 px-2.5 py-0.5 rounded-full text-xs font-medium
                            ${task.priority === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                              task.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                              'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300'}`}
                          >
                            {task.priority === 'high' ? 'Cao' : 
                             task.priority === 'medium' ? 'Trung bình' : 'Thấp'}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center py-8">
                    <div className="mb-4">
                      <i className="fas fa-calendar-check text-gray-400 dark:text-gray-500 text-4xl"></i>
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">Không có công việc sắp đến hạn</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Không có công việc nào đến hạn trong 7 ngày tới</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Hoạt động gần đây - Thông báo */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">Hoạt động gần đây</h2>
              </div>
              <div className="p-6">
                {recentNotifications.length > 0 ? (
                  <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                    {recentNotifications.map(notification => {
                      const isTaskNotification = notification.taskId && notification.teamId;
                      const isTeamNotification = !notification.taskId && notification.teamId;
                      return (
                        <li
                          key={notification.id}
                          className={`py-4 cursor-pointer hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded transition`}
                          onClick={() => {
                            if (isTaskNotification) {
                              navigate(`/team/${notification.teamId}/tasks?taskId=${notification.taskId}&from=dashboard`);
                            } else if (isTeamNotification) {
                              navigate(`/Teams`);
                            }
                          }}
                          style={{ textDecoration: 'none', cursor: 'pointer' }}
                          title={isTaskNotification ? 'Xem danh sách công việc của phòng ban' : (isTeamNotification ? 'Xem nhóm' : '')}
                        >
                          <div className="flex items-start">
                            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center
                              ${notification.type?.includes('team') ? 'bg-emerald-100 dark:bg-emerald-900/30' : 
                                notification.type?.includes('task') ? 'bg-blue-100 dark:bg-blue-900/30' :
                                'bg-gray-100 dark:bg-gray-700'}`}
                            >
                              <i className={`fas 
                                ${notification.type?.includes('invitation') ? 'fa-user-plus' : 
                                  notification.type?.includes('team') ? 'fa-users' : 
                                  notification.type?.includes('task') ? 'fa-tasks' : 
                                  'fa-bell'} 
                                text-lg
                                ${notification.type?.includes('team') ? 'text-emerald-600 dark:text-emerald-400' : 
                                  notification.type?.includes('task') ? 'text-blue-600 dark:text-blue-400' :
                                  'text-gray-600 dark:text-gray-400'}`}
                              ></i>
                            </div>
                            <div className="ml-4 flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {notification.title}
                              </p>
                              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                {notification.message}
                              </p>
                              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                {notification.createdAt ? formatDate(notification.createdAt) : 'Gần đây'}
                              </p>
                            </div>
                            {notification.read === false && (
                              <div className="ml-2 flex-shrink-0">
                                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="text-center py-8">
                    <div className="mb-4">
                      <i className="far fa-bell-slash text-gray-400 dark:text-gray-500 text-4xl"></i>
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">Không có thông báo gần đây</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Chúng tôi sẽ thông báo khi có điều gì đó xảy ra
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Cột phải - Thông tin phòng ban và Tóm tắt hồ sơ */}
          <div className="space-y-8">
            {/* Tóm tắt hồ sơ người dùng */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">Hồ sơ của tôi</h2>
              </div>
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-20 h-20 rounded-full overflow-hidden border-4 border-emerald-100 dark:border-emerald-900/30">
                    <img 
                      src={userData?.photoURL || 'https://cdn-icons-png.flaticon.com/512/6897/6897018.png'} 
                      alt={userData?.displayName} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://cdn-icons-png.flaticon.com/512/6897/6897018.png';
                      }}
                    />
                  </div>
                  <div className="ml-6">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">{userData?.displayName}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{userData?.email}</p>
                    {userData?.role && (
                      <p className="mt-1 text-sm font-medium text-emerald-600 dark:text-emerald-400">
                        {userData.role}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="mt-6 grid grid-cols-2 gap-4 text-center">
                  <button
                    className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg w-full focus:outline-none hover:bg-gray-100 dark:hover:bg-gray-600 transition cursor-pointer"
                    onClick={() => {
                      if (userTeams.length === 1) {
                        navigateToTask(userTeams[0].id);
                      } else {
                        navigateToTeam();
                      }
                    }}
                    title="Xem danh sách công việc"
                  >
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalTasks}</div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Công việc được giao</div>
                  </button>
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalTeams}</div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Phòng ban</div>
                  </div>
                </div>
                
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={() => navigate(`/profile/${auth.currentUser.uid}`)}
                    className="px-4 py-2 bg-emerald-500 dark:bg-emerald-600 text-white rounded-lg hover:bg-emerald-600 dark:hover:bg-emerald-700 transition-colors text-sm"
                  >
                    Xem hồ sơ đầy đủ
                  </button>
                </div>
              </div>
            </div>
            
            {/* phòng ban của tôi */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">Phòng ban của tôi</h2>
                <button
                  onClick={() => navigate('/Teams')}
                  className="text-sm text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 font-medium"
                >
                  Xem tất cả
                </button>
              </div>
              <div className="p-6">
                {userTeams.length > 0 ? (
                  <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                    {userTeams.slice(0, 5).map(team => (
                      <li key={team.id} className="py-4 hover:bg-gray-50 dark:hover:bg-gray-700 px-2 rounded-md cursor-pointer" onClick={() => navigateToTeam(team.id)}>
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0 h-10 w-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                            <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                              {team.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {team.name}
                              </p>
                              {team.private && (
                                <span className="text-gray-500 dark:text-gray-400 text-xs" title="Phòng ban riêng tư">
                                  <i className="fas fa-lock"></i>
                                </span>
                              )}
                            </div>
                            <div className="flex items-center">
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {team.membersData?.length || 0} thành viên
                              </p>
                              {team.private && (
                                <span className="ml-2 text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded-full flex items-center">
                                  <i className="fas fa-lock text-xs mr-1"></i>
                                  <span className="text-xs">Riêng tư</span>
                                </span>
                              )}
                            </div>
                          </div>
                          <i className="fas fa-chevron-right text-gray-400 dark:text-gray-600"></i>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center py-8">
                    <div className="mb-4">
                      <i className="fas fa-users text-gray-400 dark:text-gray-500 text-4xl"></i>
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">Chưa có phòng ban nào</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Tham gia hoặc tạo phòng ban để bắt đầu cộng tác
                    </p>
                    <button
                      onClick={() => navigate('/Teams')}
                      className="mt-4 px-4 py-2 bg-emerald-500 dark:bg-emerald-600 text-white rounded-lg hover:bg-emerald-600 dark:hover:bg-emerald-700 transition-colors text-sm"
                    >
                      Tạo phòng ban
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Modal chi tiết công việc */}
        {showTaskDetailsModal && selectedTaskForDetail && (
          <TaskDetailsModal
            task={selectedTaskForDetail}
            teams={userTeams}
            onClose={handleCloseTaskDetailsModal}
          />
        )}
      </div>
    </div>
  );
};

export default Dashboard;